import { createAsyncThunk } from '@reduxjs/toolkit';

import * as BillingApi from '@sophocles/api/billing';
import * as QuickBooksApi from '@sophocles/api/quickbooks';
import { addToast } from '@sophocles/features/toast';
import { parseErrors } from '@sophocles/utils/errors';

export const loadInvoice = createAsyncThunk('billing/invoices/loadOne', async (id, { rejectWithValue }) => {
  try {
    const data = await BillingApi.fetchInvoice(id);
    return data;
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});

export const editInvoice = createAsyncThunk(
  'billing/invoices/edit',
  async ({ id, ...data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingApi.patchInvoice(id, data);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Invoice modified successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const loadInvoices = createAsyncThunk('billing/invoices/loadMany', async (queryParams, { rejectWithValue }) => {
  try {
    const data = await BillingApi.fetchInvoices(queryParams);
    return data;
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});

export const markInvoicesCompleteByAccountId = createAsyncThunk(
  'billing/invoices/markComplete',
  async ({ accountId, startDate, endDate }, { dispatch, rejectWithValue }) => {
    try {
      const data = await BillingApi.markInvoicesComplete({
        account_id: accountId,
        start_date: startDate,
        end_date: endDate,
      });
      dispatch(
        addToast({
          title: 'Success',
          message: data.detail ?? 'Invoices marked complete successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const markInvoicesCompleteByIds = createAsyncThunk(
  'billing/invoices/markComplete',
  async (invoiceIds, { dispatch, rejectWithValue }) => {
    try {
      const data = await BillingApi.markInvoicesComplete({ invoice_ids: invoiceIds });
      dispatch(
        addToast({
          title: 'Success',
          message: data.detail ?? 'Invoices marked complete successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const loadBillingAccounts = createAsyncThunk(
  'billing/accounts/loadMany',
  async (queryParams, { rejectWithValue }) => {
    try {
      const data = await BillingApi.fetchBillingAccounts(queryParams);
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const loadBillingAccount = createAsyncThunk('billing/accounts/loadOne', async (id, { rejectWithValue }) => {
  try {
    const data = await BillingApi.fetchBillingAccount(id);
    return data;
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});

export const loadBillingAccountAgreements = createAsyncThunk(
  'billing/accountAgreements/loadMany',
  async (queryParams, { rejectWithValue }) => {
    try {
      const { results } = await BillingApi.fetchBillingAccountAgreements(queryParams);
      return results;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const createBillingAccountAgreement = createAsyncThunk(
  'billing/accountAgreements/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingApi.postBillingAccountAgreement(data);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Account agreement created successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const editBillingAccountAgreement = createAsyncThunk(
  'billing/accountAgreements/edit',
  async ({ id, ...data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingApi.patchBillingAccountAgreement(id, data);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Account agreement modified successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const createBillingAccountNote = createAsyncThunk(
  'billing/accountNotes/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingApi.postBillingAccountNote(data);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Account note created successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const editBillingAccountNote = createAsyncThunk(
  'billing/accountNotes/edit',
  async ({ id, ...data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingApi.patchBillingAccountNote(id, data);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Account note modified successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const deleteBillingAccountNote = createAsyncThunk(
  'billing/accountNotes/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const data = await BillingApi.deleteBillingAccountNote(id);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Account note deleted successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const loadDevicePricing = createAsyncThunk(
  'billing/devicePricing/load',
  async (queryParams, { rejectWithValue }) => {
    try {
      const data = await BillingApi.fetchDevicePricing(queryParams);
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const deleteDevicePricing = createAsyncThunk(
  'billing/devicePricing/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const data = await BillingApi.deleteDevicePricing(id);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Device pricing deleted successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const loadFeaturePricing = createAsyncThunk(
  'billing/featurePricing/load',
  async (queryParams, { rejectWithValue }) => {
    try {
      const data = await BillingApi.fetchFeaturePricing(queryParams);
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const deleteFeaturePricing = createAsyncThunk(
  'billing/featurePricing/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const data = await BillingApi.deleteFeaturePricing(id);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Feature pricing deleted successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const pushQuickBooksInvoice = createAsyncThunk(
  'billing/invoices/pushQuickBooks',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await QuickBooksApi.createQuickBooksInvoice(id);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Invoice pushed to QuickBooks successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return response;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

// Feature Pricing Config
export const loadFeaturePricingConfig = createAsyncThunk(
  'billing/featurePricingConfig/load',
  async (accountId, { rejectWithValue }) => {
    try {
      const data = await BillingApi.fetchFeaturePricingConfig(accountId);
      return { accountId, ...data };
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const saveFeaturePricingConfig = createAsyncThunk(
  'billing/featurePricingConfig/save',
  async ({ accountId, configs }, { dispatch, rejectWithValue }) => {
    try {
      const data = await BillingApi.saveFeaturePricingConfigBatch(accountId, configs);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Feature pricing configuration saved successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return { accountId, ...data };
    } catch (e) {
      const errors = parseErrors(e.response?.data);
      dispatch(
        addToast({
          title: 'Error',
          message: errors || 'Failed to save feature pricing configuration.',
          level: 'error',
          dismissible: true,
        }),
      );
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

// Device Pricing Config
export const loadDevicePricingConfig = createAsyncThunk(
  'billing/devicePricingConfig/load',
  async (accountId, { rejectWithValue }) => {
    try {
      const data = await BillingApi.fetchDevicePricingConfig(accountId);
      return { accountId, ...data };
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const saveDevicePricingConfig = createAsyncThunk(
  'billing/devicePricingConfig/save',
  async ({ accountId, configs }, { dispatch, rejectWithValue }) => {
    try {
      const data = await BillingApi.saveDevicePricingConfigBatch(accountId, configs);
      dispatch(
        addToast({
          title: 'Success',
          message: 'Device pricing configuration saved successfully.',
          level: 'success',
          dismissible: true,
        }),
      );
      return { accountId, ...data };
    } catch (e) {
      const errors = parseErrors(e.response?.data);
      dispatch(
        addToast({
          title: 'Error',
          message: errors || 'Failed to save device pricing configuration.',
          level: 'error',
          dismissible: true,
        }),
      );
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);
